/**
 * Contact Page Styles
 * MUI sx style objects for the Contact page and form
 */

// ============================================
// PAGE CONTAINER STYLES
// ============================================

/**
 * Main page background container
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const pageBackgroundSx = (theme) => ({
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, rgb(20, 20, 20), rgb(41, 48, 64), rgb(17, 17, 17))'
      : 'linear-gradient(160deg, rgb(238, 238, 245), rgb(233, 233, 255), rgb(243, 243, 248))',
  minHeight: '100vh',
});

/**
 * Page container styles
 */
export const pageContainerSx = {
  py: { xs: 4, md: 6 },
};

// ============================================
// HERO SECTION STYLES
// ============================================

/**
 * Hero section container
 */
export const heroSectionSx = {
  textAlign: 'center',
  mb: { xs: 4, md: 6 },
};

/**
 * Hero overline text
 */
export const heroOverlineSx = {
  letterSpacing: 2,
};

/**
 * Hero title
 */
export const heroTitleSx = {
  mt: 2,
  fontWeight: 400,
  maxWidth: 600,
  mx: 'auto',
};

/**
 * Hero description
 */
export const heroDescriptionSx = {
  mt: 2,
  maxWidth: 600,
  mx: 'auto',
};

// ============================================
// FORM CONTAINER STYLES
// ============================================

/**
 * Form card container
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const formCardSx = (theme) => ({
  maxWidth: 700,
  mx: 'auto',
  p: { xs: 3, md: 4 },
  borderRadius: 3,
  backgroundColor: theme.tableHeader.highlight,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(63,100,168,0.15)'}`,
});

/**
 * Form section title
 */
export const formSectionTitleSx = {
  mb: 3,
  fontWeight: 500,
};

// ============================================
// FORM FIELD STYLES
// ============================================

/**
 * Form field container
 */
export const fieldContainerSx = {
  mb: 2.5,
};

/**
 * Category selector wrapper
 */
export const categorySelectorSx = {
  mb: 3,
};

/**
 * Text field styles
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const textFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(255,255,255,0.7)',
  },
});

/**
 * Select field styles
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const selectFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(255,255,255,0.7)',
  },
});

/**
 * Message text area styles
 * @param {Object} theme - MUI theme object
 * @returns {Object} MUI sx object
 */
export const messageFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(255,255,255,0.7)',
  },
});

// ============================================
// BUTTON STYLES
// ============================================

/**
 * Submit button container
 */
export const submitButtonContainerSx = {
  mt: 3,
  display: 'flex',
  justifyContent: 'flex-end',
};

/**
 * Submit button styles
 */
export const submitButtonSx = {
  px: 4,
  py: 1.5,
};

// ============================================
// ALERT STYLES
// ============================================

/**
 * Alert container
 */
export const alertContainerSx = {
  mb: 3,
};

// ============================================
// CATEGORY CARD STYLES
// ============================================

/**
 * Category description text
 */
export const categoryDescriptionSx = {
  mt: 0.5,
  fontSize: '0.85rem',
};

// ============================================
// TWO COLUMN LAYOUT
// ============================================

/**
 * Two column grid container
 */
export const twoColumnGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2,
  mb: 2.5,
};

/**
 * Full width field (spans both columns on larger screens)
 */
export const fullWidthFieldSx = {
  gridColumn: { xs: '1', sm: '1 / -1' },
};
